import {
  DvGridComponent,
  DvPreviewerToolPanelComponent,
  DvToolbarAction,
  DvToolbarActionEvent,
} from '@devinco/ui/grid';
import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { AgGridModule } from 'ag-grid-angular';
import { TabBase } from '@core/base/tab.base';
import { DialogService } from '@core/services/dialog.service';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import { environment } from '@env/environment';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import { GridOptionsService } from '@features/vt-grid/grid-options/grid-options.service';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { GridOptions } from '@features/vt-grid/interfaces/grid-options';
import GridUtils from '@features/vt-grid/utils/grid.utils';
import { ToolPanel } from '@pages/assets-page/components/interfaces';
import { fileUploadModalConfig } from '@shared/components/file-upload-dialog/file-upload-dialog.config';
import { DamageReportStatus } from '@shared/constants/damage-report-status';
import { Entity } from '@shared/constants/entity';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { DirectivesModule } from '@shared/directives/directives.module';
import { AttachmentsService } from '@shared/services/attachments.service';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { TranslateService } from '@ngx-translate/core';
import {
  GridApi,
  GridReadyEvent,
  IRowNode,
  ModelUpdatedEvent,
  SelectionChangedEvent,
  ToolPanelDef,
  ToolPanelVisibleChangedEvent,
} from 'ag-grid-community';
import { take } from 'rxjs';
import { DamageReportAttachmentsGridService } from './damage-report-attachments-grid.service';
import { DamageReportAttachmentDto } from './interfaces';

@Component({
  selector: 'app-damage-report-attachments-tab',
  templateUrl: './damage-report-attachments-tab.component.html',
  imports: [DvGridComponent, AgGridModule, DirectivesModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [DamageReportAttachmentsGridService, GridOptionsService],
})
export class DamageReportAttachmentsTabComponent extends TabBase {
  private gridOptionsService = inject(GridOptionsService);
  private gridService = inject(DamageReportAttachmentsGridService);
  private api = inject(RestApiService);
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private snackBar = inject(DvSnackbarService);
  private attachmentsService = inject(AttachmentsService);
  private rolesAndPermissionsService = inject(UserRolesAndPermissionsService);

  private readonly colDefs = this.gridService.getColumnDefs(() =>
    this.isEditRestricted()
  );
  private gridApi: GridApi | undefined;
  private isToolPanelOpen = false;

  entityType: typeof Entity = Entity;
  gridOptions?: GridOptions;

  selectedNode = signal<IRowNode<DamageReportAttachmentDto> | undefined>(
    undefined
  );

  actions = signal<DvToolbarAction[]>([]);

  private hasShowOnlyMinePermission = toSignal(
    this.rolesAndPermissionsService.hasPermissions([
      RolesAndPermissions.DamageShowOnlyMine,
    ]),
    { initialValue: false }
  );

  private damageReportStatus = signal<string | undefined>(undefined);

  private isEditRestricted = computed(
    () =>
      this.hasShowOnlyMinePermission() &&
      this.damageReportStatus() !== DamageReportStatus.Draft
  );

  constructor() {
    super(inject(ActivatedRoute).parent!);

    this.actions.set(
      this.gridService.getToolbarActions(this.uploadFile, this.deleteFile)
    );

    effect(() => {
      const entityId = this.entityId();
      if (!entityId) return;
      this.setupGridOptions(entityId);

      this.api
        .getInfo<{ DamageReportStatus: string }>(
          Entity.DamageReport,
          entityId,
          ['DamageReportStatus']
        )
        .pipe(take(1))
        .subscribe(({ DamageReportStatus: status }) =>
          this.damageReportStatus.set(status)
        );
    });

    effect(() => {
      const hasSelection = !!this.selectedNode();
      const editRestricted = this.isEditRestricted();

      this.actions.update(actions =>
        actions.map(action => {
          if (action.icon === 'download') {
            return { ...action, disabled: !hasSelection || editRestricted };
          }
          if (action.icon === 'delete') {
            return { ...action, disabled: !hasSelection || editRestricted };
          }
          if (action.icon === 'add_box') {
            return { ...action, disabled: editRestricted };
          }
          return action;
        })
      );
    });
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api;
  }

  onSelectionChanged({ api }: SelectionChangedEvent): void {
    const selectedNode = api.getSelectedNodes()[0] as
      | IRowNode<DamageReportAttachmentDto>
      | undefined;
    this.selectedNode.set(selectedNode);

    const data = selectedNode?.data;
    if (data?.DocumentID && this.isToolPanelOpen) {
      this.attachmentsService.getAttachmentPreview(
        api,
        data.DocumentID,
        data.FileName || ''
      );
    }
  }

  onModelUpdated(event: ModelUpdatedEvent): void {
    event.api.getDisplayedRowAtIndex(0)?.setSelected(true);
  }

  toolPanelVisibilityChanged(event: ToolPanelVisibleChangedEvent): void {
    this.isToolPanelOpen = event.api.isToolPanelShowing();
    const data = this.selectedNode()?.data;

    if (data?.DocumentID && this.isToolPanelOpen) {
      this.attachmentsService.getAttachmentPreview(
        this.gridApi!,
        data.DocumentID,
        data.FileName || ''
      );
    }
  }

  private uploadFile = (): void => {
    const entityId = this.entityId();
    if (!entityId) return;

    this.dialogService
      .open(
        fileUploadModalConfig({
          entityId,
          url: `${environment.endpoints.restApi}/attachment/${Entity.DamageReport}/${entityId}`,
        })
      )
      .closed.pipe(take(1))
      .subscribe(({ responseData, source }) => {
        if (responseData && source === 'confirm') {
          this.gridOptions?.context.loadClientSideData?.(
            this.gridOptions.context.extraFilter,
            true
          );
        }
      });
  };

  private deleteFile = (event?: DvToolbarActionEvent): void => {
    this.attachmentsService
      .openAttachmentDeleteDialog()
      .closed.pipe(take(1))
      .subscribe(({ source }) => {
        if (!event || source !== 'confirm') return;

        const { api } = event;
        const colDefs = api.getColumns()?.map(c => c.getColDef());
        const { field } =
          colDefs?.find(
            c => c.type?.includes(ColumnType.Id) || c.type?.[0] === 'id'
          ) || {};
        if (!field) return;

        const selectedNodes: IRowNode[] = api.getSelectedNodes();
        if (!selectedNodes.length) return;

        const { data, rowIndex } = selectedNodes[0];

        this.api.deleteCustom(`Attachment/${data[field]}`).subscribe(() => {
          this.snackBar.open(
            this.translate.instant('General.DeleteActionSuccessful'),
            SnackBarConfig.Success
          );
          api.applyTransaction({ remove: [data!] });
          const lastIndex = api.getLastDisplayedRowIndex();
          const nextIndex = lastIndex > rowIndex! ? rowIndex! : lastIndex;
          api.getDisplayedRowAtIndex(nextIndex)?.setSelected(true, true);
        });
      });
  };

  private getFilterModel(entityId: string): FilterModelWithKeys {
    return {
      DamageReportID: {
        filterType: 'number',
        type: 'equals',
        filter: entityId,
      },
    };
  }

  private setupGridToolPanel(): void {
    GridUtils.addToolPanelIfMissing(
      this.gridOptions,
      {
        id: 'preview',
        labelDefault: 'Preview',
        labelKey: 'Preview',
        iconKey: 'preview',
        toolPanel: DvPreviewerToolPanelComponent,
        width: 400,
      } as ToolPanelDef,
      '<span class="dv-icons">eye</span>'
    );
  }

  private setupGridOptions(entityId: string): void {
    if (!this.gridOptions) {
      this.gridOptions = this.gridOptionsService.clientSide(
        this.colDefs,
        Entity.DamageReportDocument,
        this.getFilterModel(entityId)
      );
      this.setupGridToolPanel();
    } else {
      this.gridOptions.context.loadClientSideData?.(
        this.getFilterModel(entityId)
      );
      this.gridOptions.context.extraFilter = this.getFilterModel(entityId);

      (
        this.gridApi?.getToolPanelInstance('preview') as ToolPanel | undefined
      )?.api.setMediaType('');
    }
  }
}
